/**
 * CK | Variant picker popup for cart upsell
 * Custom element: <ck-variant-picker>
 *
 * Listens for 'ck:cart-upsell:open-picker', fetches /products/{handle}.js,
 * renders option buttons, and adds the selected variant to cart on confirm.
 */
(function () {
  if (window.customElements && customElements.get('ck-variant-picker')) return;

  class CkVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.body = this.querySelector('[data-ck-body]');
      this.backdrop = this.querySelector('[data-ck-backdrop]');
      this.closeBtn = this.querySelector('[data-ck-close]');
      this.live = this.querySelector('[data-ck-live]');
      this.sheet = this.querySelector('.ck-variant-picker__sheet');
      this.cartDrawer = this.closest('#CartDrawer');
      this.openToken = 0;
      this._adding = false;
      this._loading = false;
      this._opening = false;

      this.onOpen = this.onOpen.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSheetPointerDown = this.onSheetPointerDown.bind(this);
      this.onDrawerClassMutation = this.onDrawerClassMutation.bind(this);

      document.addEventListener('ck:cart-upsell:open-picker', this.onOpen);
      if (this.backdrop) this.backdrop.addEventListener('click', this.onClose);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onClose);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeyDown);
      if (this.sheet) this.sheet.addEventListener('pointerdown', this.onSheetPointerDown);
      if (this.cartDrawer) {
        this.drawerObserver = new MutationObserver(this.onDrawerClassMutation);
        this.drawerObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('ck:cart-upsell:open-picker', this.onOpen);
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.sheet) this.sheet.removeEventListener('pointerdown', this.onSheetPointerDown);
      if (this.drawerObserver) {
        this.drawerObserver.disconnect();
        this.drawerObserver = null;
      }
    }

    onOpen(e) {
      const { handle, originButton } = e.detail || {};
      if (!handle) return;
      this.openToken += 1;
      const token = this.openToken;
      this.originButton = originButton || null;
      this.lastFocus = document.activeElement;
      this.selected = [];
      this._loading = true;
      this._opening = true;
      this.classList.add('is-loading');
      if (this.cartDrawer) this.cartDrawer.classList.add('ck-picker-open');
      this.removeAttribute('hidden');
      this.removeAttribute('aria-hidden');

      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error(res.statusText);
          return res.json();
        })
        .then((product) => {
          if (token !== this.openToken) return;
          this.product = product;
          this.selected = product.options.map(() => null);
          this.renderBody(product);
          this.open();
        })
        .catch(() => {
          if (token !== this.openToken) return;
          if (this.originButton) {
            const upsell = this.originButton.closest('ck-cart-upsell');
            if (upsell) {
              const liveEl = upsell.querySelector('[data-ck-live]');
              if (liveEl) liveEl.textContent = this.dataset.strError;
            }
          }
          this.hideShell();
        })
        .finally(() => {
          if (token !== this.openToken) return;
          this._loading = false;
          this.classList.remove('is-loading');
        });
    }

    open() {
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const onDone = () => {
          this._opening = false;
          if (this.closeBtn) this.closeBtn.focus(); else if (this.sheet) this.sheet.focus();
        };
        this.waitForSheetTransition(onDone);
      });
    }

    onClose(e) {
      if (e) e.preventDefault();
      if (this._loading || this._opening || this._adding || this.hasAttribute('hidden')) return;
      this.classList.remove('is-open');
      this.waitForSheetTransition(() => this.hideShell(true));
    }

    onKeyDown(e) {
      if (this.hasAttribute('hidden')) return;

      if (e.key === 'Escape') {
        this.onClose();
        return;
      }

      // Trap Tab focus inside the sheet
      if (e.key === 'Tab') {
        const focusable = Array.from(
          this.sheet.querySelectorAll(
            'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.closest('[hidden]'));

        if (!focusable.length) { e.preventDefault(); return; }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    onClick(e) {
      if (this._loading || this._opening || this._adding) return;
      const optBtn = e.target.closest('[data-ck-option]');
      if (optBtn) {
        this.selectOption(optBtn);
      }
    }

    onSheetPointerDown(e) {
      // Prevent pointer events inside sheet from being interpreted as backdrop taps.
      e.stopPropagation();
    }

    onDrawerClassMutation() {
      if (!this.cartDrawer) return;
      const drawerIsOpen = this.cartDrawer.classList.contains('drawer--is-open');
      if (!drawerIsOpen && !this.hasAttribute('hidden')) {
        this.forceHide();
      }
    }

    getOptionName(option) {
      return typeof option === 'object' && option !== null ? option.name : option;
    }

    renderBody(product) {
      const price = this.formatMoney(product.price_min);
      const img = product.featured_image
        ? '<img src="' + this.escapeHtml(product.featured_image) + '" alt="' + this.escapeHtml(product.title) + '" width="64" height="64" class="ck-variant-picker__product-img" loading="lazy">'
        : '';

      this.optionNames = product.options.map((o) => this.getOptionName(o));

      let optionGroupsHtml = '';
      this.optionNames.forEach((optName, optIdx) => {
        const values = [...new Set(product.variants.map((v) => v.options[optIdx]))];
        const btnsHtml = values
          .map((val) => {
            const variantsForThis = product.variants.filter((v) => v.options[optIdx] === val);
            const anyAvailable = variantsForThis.some((v) => v.available);
            return '<button type="button" class="ck-variant-picker__option-btn" data-ck-option data-option-index="' + optIdx + '" data-value="' + this.escapeHtml(val) + '" aria-pressed="false"' + (anyAvailable ? '' : ' disabled aria-disabled="true"') + '>' + this.escapeHtml(val) + '</button>';
          })
          .join('');

        optionGroupsHtml += '<div class="ck-variant-picker__option-group" data-ck-option-group data-option-index="' + optIdx + '"><div class="ck-variant-picker__option-label">' + this.escapeHtml(optName) + '</div><div class="ck-variant-picker__option-btns">' + btnsHtml + '</div></div>';
      });

      const selectedHtml = this.optionNames
        .map((name, i) => '<span class="ck-variant-picker__selected-opt" data-ck-selected-opt="' + i + '">' + this.escapeHtml(name) + ': —</span>')
        .join('');

      this.body.innerHTML =
        '<div class="ck-variant-picker__product-header">' +
          img +
          '<div class="ck-variant-picker__product-info">' +
            '<div class="ck-variant-picker__product-title_wrapper">' +
              '<p id="ck-vp-title" class="ck-variant-picker__product-title">' + this.escapeHtml(product.title) + '</p>' +
              '<p class="ck-variant-picker__product-price">' + price + '</p>' +
            '</div>' +
            '<div class="ck-variant-picker__selected-options">' + selectedHtml + '</div>' +
          '</div>' +
        '</div>' +
        optionGroupsHtml;
    }

    selectOption(btn) {
      const idx = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.value;
      this.selected[idx] = value;

      const group = btn.closest('[data-ck-option-group]');
      group.querySelectorAll('[data-ck-option]').forEach((b) => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      this.updateSelectedIndicator(idx, value);
      this.tryAutoAdd();
    }

    updateSelectedIndicator(idx, value) {
      var el = this.body.querySelector('[data-ck-selected-opt="' + idx + '"]');
      if (!el || !this.optionNames) return;
      el.textContent = this.optionNames[idx] + ': ' + value;
    }

    tryAutoAdd() {
      const allChosen = this.selected.every((s) => s !== null);
      if (!allChosen) return;

      const variant = this.product.variants.find((v) =>
        v.options.every((opt, i) => opt === this.selected[i])
      );

      if (!variant || !variant.available) {
        this.announceError(this.dataset.strSoldout || 'Sold out');
        return;
      }

      this.submitAdd(variant.id);
    }

    submitAdd(variantId) {
      this._adding = true;
      this.body.querySelectorAll('[data-ck-option]').forEach((b) => { b.disabled = true; });

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => { throw new Error((err && err.description) || res.statusText); });
          }
          return res.json();
        })
        .then(() => {
          // Close after successful add even though request lifecycle just ended.
          this._adding = false;
          this.onClose();
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[ck-variant-picker] add failed', err);
          this.announceError(this.dataset.strError || 'Error');
          this.body.querySelectorAll('[data-ck-option]').forEach((b) => { b.disabled = false; });
        })
        .finally(() => {
          this._adding = false;
        });
    }

    waitForSheetTransition(done) {
      if (!this.sheet) {
        done();
        return;
      }
      let finished = false;
      const complete = () => {
        if (finished) return;
        finished = true;
        this.sheet.removeEventListener('transitionend', onEnd);
        done();
      };
      const onEnd = (evt) => {
        if (evt.target === this.sheet) complete();
      };
      this.sheet.addEventListener('transitionend', onEnd);
      setTimeout(complete, 320);
    }

    hideShell(restoreFocus) {
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      this.body.innerHTML = '';
      this.classList.remove('is-open', 'is-loading');
      this._opening = false;
      this._loading = false;
      if (this.cartDrawer) this.cartDrawer.classList.remove('ck-picker-open');
      if (restoreFocus && this.lastFocus) this.lastFocus.focus();
    }

    forceHide() {
      this.openToken += 1;
      this._adding = false;
      this.hideShell(false);
    }

    announceError(msg) {
      if (!this.live) return;
      this.live.textContent = '';
      void this.live.offsetWidth;
      this.live.textContent = msg;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const num = cents / 100;
      const amount = num.toFixed(2);
      const amountNoDecimals = Math.round(num).toString();

      const withComma = amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const noDecComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const withApostrophe = amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      const noDecSpace = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

      return format
        .replace('{{amount_with_comma_separator}}', withComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecComma)
        .replace('{{amount_with_apostrophe_separator}}', withApostrophe)
        .replace('{{amount_no_decimals_with_space_separator}}', noDecSpace)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);
    }

    escapeHtml(str) {
      if (typeof str !== 'string') return str;
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('ck-variant-picker', CkVariantPicker);
})();
